/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { NodesApi } from '@alfresco/js-api';
import { AppStore, SetCurrentUrlAction } from '@alfresco/aca-shared/store';
import { from, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AcaConfigFolderService } from '../shared/aca-config-folder.service';

/**
 * Whether the workflow feature — the "งานของฉัน" and "การอนุมัติ" navigation
 * entries and the start-workflow actions — is offered at all.
 *
 * The flag is kept where branding and the watermark keep theirs: one small
 * document in the repository — `Data Dictionary/ACA/workflow.json` — which
 * every signed-in user can read (Data Dictionary grants Consumer to
 * GROUP_EVERYONE) and only an administrator can write. That makes the feature
 * a system-wide policy rather than a per-user preference: the administrator
 * decides once, on the settings page, and every reader gets the same answer.
 *
 * When the document is missing or cannot be read the feature stays ON — a
 * deployment that never set the flag is the deployment the application
 * shipped with, so the safe fallback is the menus that were always there.
 */
@Injectable({ providedIn: 'root' })
export class WorkflowSettingsService {
  private readonly api = inject(AlfrescoApiService);
  private readonly configFolder = inject(AcaConfigFolderService);
  private readonly store = inject<Store<AppStore>>(Store);
  private readonly router = inject(Router);

  private static readonly FOLDER_PATH = 'Data Dictionary/ACA';
  private static readonly FILE_NAME = 'workflow.json';

  /** Whether the workflow menus and actions are visible. Default: on. */
  readonly enabled = signal(true);

  private loaded = false;
  private readonly loadedSubject = new ReplaySubject<void>(1);

  /** Emits once the stored flag has been read — the route guard waits on it. */
  readonly whenLoaded: Observable<void> = this.loadedSubject.asObservable();

  /** Reads the flag once per session. */
  load(): void {
    if (this.loaded) {
      return;
    }
    this.loaded = true;

    this.read()
      .pipe(catchError(() => of(null)))
      .subscribe((saved) => {
        if (saved !== null) {
          this.enabled.set(saved);
        }
        this.loadedSubject.next();
        this.refreshNavigation();
      });
  }

  /** Writes the flag for everyone. Requires an administrator. */
  setEnabled(enabled: boolean): Observable<void> {
    return this.nodeId(true).pipe(
      switchMap((nodeId) => from(this.nodes().updateNodeContent(nodeId, JSON.stringify({ enabled })) as Promise<any>)),
      tap(() => {
        this.enabled.set(enabled);
        this.refreshNavigation();
      }),
      map(() => undefined)
    );
  }

  /**
   * The sidenav builds its menu from the store's navigation state, so the
   * `app.workflowEnabled` rule only re-runs when that state emits. Storing
   * the current url again is a no-op for everyone else and just enough to
   * make the menu re-evaluate — without it a saved flag would wait for the
   * next navigation to take effect.
   */
  private refreshNavigation(): void {
    this.store.dispatch(new SetCurrentUrlAction(this.router.url));
  }

  private read(): Observable<boolean | null> {
    return this.nodeId(false).pipe(
      switchMap((nodeId) => (nodeId ? from(this.nodes().getNodeContent(nodeId) as Promise<any>) : of(null))),
      switchMap((content: any) => (content ? this.text(content) : of(null))),
      map((text) => {
        if (!text) {
          return null;
        }
        try {
          const parsed = JSON.parse(text) as { enabled?: boolean };
          return parsed.enabled !== false;
        } catch {
          // a hand-edited or truncated document must not hide the feature
          return null;
        }
      })
    );
  }

  /** A Blob on the browser, but the API's typings promise only `any`. */
  private text(content: any): Observable<string | null> {
    if (typeof content === 'string') {
      return of(content);
    }
    if (content instanceof Blob) {
      return from(content.text());
    }
    return of(null);
  }

  /**
   * The document's node id, creating the folder and the document on the way
   * when `create` is set — which only ever happens on an administrator's save.
   */
  private nodeId(create: boolean): Observable<string | null> {
    const nodes = this.nodes();

    // listed once for every settings document — see AcaConfigFolderService
    return this.configFolder.find(WorkflowSettingsService.FILE_NAME).pipe(
      catchError(() => {
        if (!create) {
          return of(null);
        }

        // the folder may or may not be there; either way the document is not
        return from(nodes.createNode('-root-', { name: 'ACA', nodeType: 'cm:folder', relativePath: 'Data Dictionary' } as any) as Promise<any>).pipe(
          catchError(() => of(null)),
          switchMap(() =>
            from(
              nodes.createNode('-root-', {
                name: WorkflowSettingsService.FILE_NAME,
                nodeType: 'cm:content',
                relativePath: WorkflowSettingsService.FOLDER_PATH
              } as any) as Promise<any>
            )
          ),
          map((entry: any) => entry?.entry?.id as string),
          tap(() => this.configFolder.forget())
        );
      })
    );
  }

  private nodes(): NodesApi {
    return new NodesApi(this.api.getInstance() as any);
  }
}
